import { Component, OnInit, ViewChild, Input, Output, EventEmitter, OnChanges, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { enmEFormViewModes, EFormConfiguration, PageObjectTypes } from '../../../shared-components/e-forms/e-form-config.model';
import { EFormControl } from '../../../shared-components/e-forms/e-form-control.model';
import { CustomDesignPage } from '../../../admin360/customization/models/custom-design-page.model';
import { EFormViewerComponent } from '../../../shared-components/e-forms/e-form-viewer/e-form-viewer.component';
import { CustomizationService } from '../../../admin360/customization/customization.service';
import { CustomizationFrameworkService } from '../customization-framework.service';
import { EFormService } from '../../../shared-components/e-forms/e-form.service';
import { CustomDataTemplateRef } from '../models/custom-data-template-ref.model';
import { ErrorList } from '../../../shared-components/controls/error-prompt/error-list.model';
import { CSTField, CustomFieldDataObject } from '../models/custom-data-field.model';
import { YearService } from '../../../shared-components/year/year.service';
import { take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { SecurityService } from '../../../shared-components/security/security.service';
import { ViewDesignPagesModalComponent } from '../view-design-pages/view-design-pages-modal/view-design-pages-modal.component';
import { LoginService } from '../../login/login.service';
import { EFormDataObject } from '../../../shared-components/e-forms/e-formDataObject.model';
import { cloneDeep } from 'lodash';


@Component({
  selector: 'app-design-page-viewer',
  templateUrl: './design-page-viewer.component.html',
  styleUrls: ['./design-page-viewer.component.css']
})
export class DesignPageViewerComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  validationTriggerSubscription: Subscription;
  config: EFormConfiguration = new EFormConfiguration();
  designPageIndex: number;
  showViewer = false;
  userCanEditPage = false;
  tabGroupIsGettingMetadata = false;
  userControlsInitialized = false;
  systemControlsInitialized = false;

  private _designPage: CustomDesignPage;
  public observabledesignPage = new Subject<CustomDesignPage>();
  observabledesignPageStream$ = this.observabledesignPage.asObservable();
  public get designPage(): CustomDesignPage {
  return this._designPage;
  }
  public set designPage(value: CustomDesignPage) {
    this._designPage = value;
    this.observabledesignPage.next(value);
    this.observabledesignPage.complete();
  }

  private _dataValues: any[] = [];
  private _contentTemplates: CustomDataTemplateRef[];

  // custom templates defined in the parent component.  You should always use the built-in custom fields when possible, but if you
  // need special functionality that the e-Form Designer does not support, you can define your own templates to inject into the form
  @Input() set contentTemplates(contentTemplates: CustomDataTemplateRef[]) {
    this._contentTemplates = contentTemplates;
    this.updateContentTemplates();
  }

  get contentTemplates(): CustomDataTemplateRef[] {
    return this._contentTemplates;
  }

  // This is the data that will be used to look up and store data for custom fields on this page.
  @Input() masterId: number;
  @Input() hideTitle = false;

  // this object contains the data for the default fields on this design page.
  // two-way binding is used to maintain data between the design page and the parnt component.
  @Input() set dataValues(object: any) {
    this._dataValues = object;
    this.updateDataValues();
  }

  get dataValues(): any {
    return this._dataValues;
  }

  @Input() ignoreRequiredFields: boolean;

  @Output() dataValuesChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>(); // emits when any value is changed on the page
  @Output() pageLoaded: EventEmitter<any> = new EventEmitter<any>();  // emits once the page has finished loading
  @Output() validationErrors: EventEmitter<ErrorList[]> = new EventEmitter<ErrorList[]>();  // emits errors found during validation
  @Output() eFormViewerInitialized = new EventEmitter<boolean>();  // emits when the e-Form Viewer has loaded
  @Output() templateChanged = new EventEmitter<boolean>();  // emits when a user edits the template or selects a different version of this design page.
  @Output() pageRetrieved = new Subject<boolean>();  // emits when the design page has been retreived from the DB.
  @Input() set validationTrigger(trigger: EventEmitter<any>) {  // triggers validation on the page
    if (this.validationTriggerSubscription) {
      const subIndex = this.subscriptions.indexOf(this.validationTriggerSubscription);
      if (subIndex > -1) {
        this.subscriptions.splice(subIndex, 1);
      }
      this.validationTriggerSubscription.unsubscribe();
    }
    this.validationTriggerSubscription = trigger.subscribe(o => {
      const errors = this.validateControls();
      this.validationErrors.emit(errors);
    });
    this.subscriptions.push(this.validationTriggerSubscription);
  }

  @Input() designPageID: number;  // CST_DesignPages.ID
  @Input() set manualDataUpdateTrigger(trigger) {  // allows the parent page to force the design page to refresh data values
    this.updateDataValues();
  }

  private _readOnly: boolean;
  public get readOnly(): boolean {
    return this._readOnly;
  }
  @Input() public set readOnly(value: boolean) {  // forces page into read-only mode
    this._readOnly = value;
    if (this.config) {
      this.config.ReadOnly = value;
    }
    if (this.eFormViewer && !value) {
      this.eFormViewer.editable = false;
    }
  }

  private _isDirty: boolean;
  public get isDirty(): boolean {
    return this._isDirty;
  }
  @Input() public set isDirty(value: boolean) { // sets dirty flag for page
    this._isDirty = value;
  }
  @Output() isDirtyChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _eFormViewer: EFormViewerComponent;
  @ViewChild(EFormViewerComponent, {static: false}) set eFormViewer(eFormViewer: EFormViewerComponent) {
    this._eFormViewer = eFormViewer;
    if (eFormViewer) {
      this.getCustomDataFieldMetaData();
    }
  }
  get eFormViewer(): EFormViewerComponent {
    return this._eFormViewer;
  }

  @ViewChild(EFormViewerComponent) set content(content: EFormViewerComponent) {
    this.eFormViewer = content;
    this.updateDataValues();
  }

  metadataRetrieved = false;
  private _dataStore: CustomFieldDataObject[] = [];
  public get dataStore(): CustomFieldDataObject[] {
      return this._dataStore;
  }
  @Input() public set dataStore(value: CustomFieldDataObject[]) {
      this._dataStore = value;
      if (this.config) {
          this.config.CFDataStore = value;
      }
  }

  constructor(
    private customizationService: CustomizationService,
    private customizationFrameworkService: CustomizationFrameworkService,
    private eFormService: EFormService,
    private yearService: YearService,
    private dialog: MatDialog,
    private securitService: SecurityService,
    private loginService: LoginService
  ) {
    this.designPageIndex = this.customizationFrameworkService.designPageIndexIncrementor;
    this.customizationFrameworkService.designPageIndexIncrementor++;
    this.eFormService.observableControlValueChangedStream$.subscribe(c => {
      if (c.DesignPageIndex == this.designPageIndex) {
        if (this.userControlsInitialized && this.systemControlsInitialized) {
          this.isDirty = true;
          this.isDirtyChange.emit(true);
        }
      }
    });
  }

  ngOnInit() {
    this.getDesignPages();

    this.subscriptions.push(this.customizationFrameworkService.observablesBasePageOnly.subscribe(o => {
      this.getDesignPages();
    }));

    this.config.HideTitle = this.hideTitle;
    this.config.ReadOnly = this.readOnly;
    this.userCanEditPage = this.securitService.IsAllPermissionCodeAssigned('SUI_MDP') && this.loginService.LoginSettings.EnableCustomizationFramework;

    this.subscriptions.push(this.yearService.observableDefaultYearStream$.subscribe(o => {
        this.getControlValues();
    }));

    this.subscriptions.push(this.customizationFrameworkService.observableFieldValuesRetrievedStream$.subscribe(fields => {
        this.getControlValues(fields);
    }));

    this.subscriptions.push(this.customizationFrameworkService.observableobservableFieldMetaDataRetrievedStream$.subscribe(o => {
      if (this.tabGroupIsGettingMetadata) {
        this.tabGroupIsGettingMetadata = false;
        if (!this.metadataRetrieved) {
          this.getCustomDataFieldMetaData();
        }
      }
  }));
  }

  private getDesignPages() {
    this.showViewer = false;
    this.subscriptions.push(this.customizationService.getDesignPageByIDs(this.customizationFrameworkService.basePageOnly, [this.designPageID]).subscribe(pages => {
      if (pages && pages.length == 1) {
        this.designPage = pages[0];
        this.loadViewer();
      }
    }));
  }

  updateDataValues() {
    if (this.eFormViewer && this._dataValues) {
      setTimeout(() => {
        this.systemControlsInitialized = false;
        const controls = this.eFormViewer.getAllCustomFields(this.designPageIndex);

        controls.forEach(control => {
          if (control.CustomFieldRefName && control.CustomField && control.CustomFieldName && !control.CustomFieldAllowControlToHandleOwnData) {
            if (this._dataValues.hasOwnProperty(control.CustomFieldRefName)) {
              control.Value = this._dataValues[control.CustomFieldRefName];
            }
            else {
              control.Value = null;
            }
            this.eFormService.controlValueChanged(control);
          }
        });
        this.systemControlsInitialized = true;
      }, 0);
    }
  }

  updateContentTemplates() {
    if (!this.eFormService.contentTemplates) {
      this.eFormService.contentTemplates = [];
    }

    this._contentTemplates.forEach(template => {
      if (this.designPageIndex) {
        template.DesignPageIndex = this.designPageIndex;
        this.eFormService.contentTemplates.push(template);
      }
    });
  }

  valueChanged(refName: string) {
    const control: EFormControl = this.getControlByRefName(refName);
    if (control) {
      if (!this.dataValues) {
        this.dataValues = {};
      }
      this.dataValues[control.CustomFieldRefName] = control.Value;
      this.valueChange.emit();
      this.dataValuesChange.emit(this.dataValues);
    }
  }

  renderCompleted() {
    this.pageLoaded.emit(true);
  }

  loadViewer() {
    this.config.Title = this.designPage.Name;
    this.config.UseInstance = false;
    this.config.ShowSubmittedBy = false;
    this.config.ViewMode = enmEFormViewModes.Page;
    this.config.AllowPages = false;
    this.config.AllowTabs = false;
    this.config.IgnoreRequiredFields = this.ignoreRequiredFields;
    this.config.CFDataStore = this.dataStore;
    switch (this.designPage.ObjectName.toLowerCase()) {
      case 'student':
        this.config.PageObjectType = PageObjectTypes.Student;
        break;
      case 'staff':
        this.config.PageObjectType = PageObjectTypes.Staff;
        break;
      case 'other':
        this.config.PageObjectType = PageObjectTypes.Other;
        break;
    }
    this.setFormData();
  }

  setFormData() {
    this.customizationFrameworkService.reconcileChangesToDesignPage(this.designPage).pipe(take(1)).subscribe(formData => {
      this.designPage.FormData = JSON.stringify(formData);
      this.designPage.Version = this.designPage.BaseVersion;
      this.eFormService.selectedFormData = formData;
      this.showViewer = true;
    });
  }

  getAllCustomFields(): EFormControl[] {
    if (this.eFormViewer) {
      return this.eFormViewer.getAllCustomFields(this.designPageIndex);
    }
    else {
      let formData = new EFormDataObject();
      try {
          formData = JSON.parse(this.designPage.FormData);
      } catch (err) {
          formData = new EFormDataObject();
      }

      const controls = this.eFormService.getAllFormControls(formData).filter(c => c.CustomField && c.CustomFieldName);
      return controls;
    }
  }

  getValueByRefName(refName: string): any {
    let value: any;

    const control: EFormControl = this.eFormViewer.getCustomFieldByRefName(refName, this.designPageIndex);

    if (control) {
      value = control.Value;
    }
    return value;
  }

  getControlByRefName(refName: string): EFormControl {
    return this.eFormViewer ? this.eFormViewer.getCustomFieldByRefName(refName, this.designPageIndex) : null;
  }

  setValueByRefName(value: any, refName: string) {
    this.eFormViewer.setCustomFieldByRefName(value, refName, this.designPageIndex);
  }

  setEditableByRefName(refName: string, editable: boolean) {
    const control = this.getControlByRefName(refName);

    if (control) {
      control.Editable = editable;
      this.eFormService.observableReadOnlyStatusChanged.next(control);
    }
  }

  validateControls(): ErrorList[] {
    this.eFormViewer.validateButton.nativeElement.click();
    let errors: ErrorList[] = [];

    const controls: EFormControl[] = this.getAllCustomFields();

    controls.forEach(c => {
      const field = this.customizationFrameworkService.customDataFields.find(o => o.FieldName == c.CustomFieldName);
      if (this.customizationFrameworkService.userCanEditField(field, c.Editable && !this.readOnly, c.CustomFieldAllowControlToHandleOwnData)) {
        errors = errors.concat(c.validateFunction());
      }
    });

    return errors;
  }

  initializedEFormViewerEvent() {
    this.eFormViewerInitialized.emit(true);
  }

  ngOnDestroy() {
    const controlsToRemove: EFormControl[] = this.eFormService.allCustomFieldControls.filter(control => control.DesignPageIndex == this.designPageIndex);
    const otherControlsToRemove: EFormControl[] = this.eFormService.allFormControls.filter(control => control.DesignPageIndex == this.designPageIndex);

    controlsToRemove.forEach(controlToRemove => {
      let index: number = this.eFormService.allCustomFieldControls.findIndex(control => control.Id == controlToRemove.Id);
      if (index > -1) {
        this.eFormService.allCustomFieldControls.splice(index, 1);
      }

      index = this.eFormService.allFormControls.findIndex(control => control.Id == controlToRemove.Id);
      if (index > -1) {
        this.eFormService.allFormControls.splice(index, 1);
      }
    });

    if (this.eFormService.contentTemplates) {
        const templatesToRemove: CustomDataTemplateRef[] = this.eFormService.contentTemplates.filter(template => template.DesignPageIndex == this.designPageIndex || !template.DesignPageIndex);
    
        templatesToRemove.forEach(templateToRemove => {
          const index: number = this.eFormService.contentTemplates.findIndex(template => template.RefName == templateToRemove.RefName);
          if (index > -1) {
            this.eFormService.contentTemplates.splice(index, 1);
          }
        });
    }
  }

  getControlValues(fields: CSTField[] = null): EFormControl[] {
    this.isDirty = false;
    this.userControlsInitialized = false;
    let controls: EFormControl[] = [];
    // we only get controls that the user has added to the page (control handles own data)
    controls = this.getAllCustomFields().filter(o => o.CustomFieldAllowControlToHandleOwnData);
    controls.forEach(controlToUpdate => {
      controlToUpdate.CustomFieldMasterId = this.masterId;
        const field = cloneDeep(this.customizationFrameworkService.customDataFields.find(f => f.FieldName == controlToUpdate.CustomFieldName));
        if (controlToUpdate.CustomFieldAllowControlToHandleOwnData && field && this.customizationFrameworkService.userCanViewField(field, controlToUpdate.Editable, controlToUpdate.CustomFieldAllowControlToHandleOwnData)) {
            const dataObj = this.getCustomFieldDataObject(field);
            if (dataObj && dataObj.Value != null) {
                field.Value = dataObj.Value;
                if (controlToUpdate) {
                    controlToUpdate.Value = dataObj.Value;
                }
            }
            else if (this.masterId > 0 && fields && fields.find(f => controlToUpdate.CustomFieldName == f.FieldName && f.MasterId == this.masterId)) {
                const fieldWithValue = fields.find(f => controlToUpdate.CustomFieldName == f.FieldName && f.MasterId == this.masterId);
                if (fieldWithValue) {
                    controlToUpdate.Value = fieldWithValue.Value;
                    this.persistValue(field, controlToUpdate);
                }
            }
            else {
                controlToUpdate.Value = null;
            }
        }
    });
      setTimeout(() => {
        this.userControlsInitialized = true;
      }, 0);
    return controls;
}

  getCustomFieldDataObject(field: CSTField): CustomFieldDataObject {
    return this.customizationFrameworkService.getCustomFieldDataObject(field, this.masterId, this.dataStore);
  }

  persistValue(field: CSTField, control: EFormControl) {
    this.customizationFrameworkService.persistValue(field, control.Editable, control.CustomFieldAllowControlToHandleOwnData, this.masterId, this.dataStore, false);
  }

  getCustomDataFieldMetaData() {
    if (!this.metadataRetrieved && !this.tabGroupIsGettingMetadata) {
      this.metadataRetrieved = true;
      const fieldNames: string[] = [];
      const controls = this.getAllCustomFields();
      controls.forEach(c => {
        const field = this.customizationFrameworkService.customDataFields.find(o => o.FieldName == c.CustomFieldName);
        if (!field && c.CustomFieldName) {
          fieldNames.push(c.CustomFieldName);
        }
      });
      if (fieldNames.length > 0) {
        this.customizationFrameworkService.massGetCustomDataFieldByFieldName(fieldNames, this.yearService.defaultYear.Id, this.yearService.defaultYear.SchoolID).pipe(take(1)).subscribe(fields => {
          fields.forEach(f => {
            if (!this.customizationFrameworkService.customDataFields.find(o => o.FieldName == f.FieldName)) {
              this.customizationFrameworkService.customDataFields.push(f);
            }
            if (f.IsDataDef && f.DataDef) {
              if (!this.customizationFrameworkService.dataDefinitionLists.find(o => o.Enum == f.DataDef.Enum)) {
                this.customizationFrameworkService.dataDefinitionLists.push(f.DataDef);
              }
              f.DataDef.DataDefinitions.forEach(dd => {
                dd.YearId = this.yearService.defaultYear.Id;
                dd.SiteYearId = this.yearService.defaultYear.SiteYearId;
              });
              this.customizationFrameworkService.dataDefinitionListValues[f.DataDef.Enum] = f.DataDef.DataDefinitions;
            }
          });
          this.customizationFrameworkService.observableFieldMetaDataRetrieved.next();
          this.getControlValues();
        });
      }
      else {
        this.getControlValues();
      }
    }
  }

  editPageClick() {
    this.subscriptions.push(this.customizationService.getDesignPageByIDs(false, [this.designPageID], true).subscribe(pages => {
      const pageTitle = 'Design Page';
      const dialogRef = this.dialog.open(ViewDesignPagesModalComponent, {
        height: '90vh',
        maxHeight: '90vh',
        width: '80vw',
        maxWidth: '80vw',
        disableClose: true,
        data: { designPageId: this.designPageID, pageTitle: pageTitle}
      });
      dialogRef.afterClosed().subscribe((isTemplateChanged: boolean) => {
        if (isTemplateChanged) {
          this.templateChanged.emit(true);
          this.getDesignPages();
        }
      });
    }));
  }
}
